import { useEffect, useRef, useState } from 'react'
import { storedCartLines, useCartActions } from '../../contexts/StoreContext'
import { useAccount } from '../../contexts/AccountContext'
import { ApiError, fetchOrderStatus, isAbortError, retryPayment } from '../../utils/api'
import { errorMessage } from '../../core/errors'
import { fromCents } from '../../core/format/currency'
import type { OrderStatusResponse } from '../../core/types/requests'
import { cartBelongsTo, clearAttempt, readPlaced, savePlaced, takePending, type PlacedOrder } from './orderStorage'

/** Elke 3 seconden vragen, hooguit een minuut: daarna rustig wachten op de mail. */
const POLL_INTERVAL_MS = 3000
const POLL_LIMIT_MS = 60_000

export type ReturnPhase = 'checking' | 'paid' | 'failed' | 'expired' | 'waiting' | 'notFound' | 'error'

/**
 * De terugkeerpagina na Mollie (/bestelling/:orderId). De terugkeer zelf bewijst niets:
 * de status komt van de server, die hem via de Mollie-webhook kent.
 * - betaald: mand en poging wissen (alleen als dit de bestelling uit déze browser is), bedankscherm;
 * - mislukt/verlopen: opnieuw betalen met dezelfde order, de mand blijft staan;
 * - open: elke 3 s opnieuw vragen, na een minuut stoppen met een rustige uitleg.
 */
export function useOrderReturn(orderId: string, token: string) {
  const { clearCart } = useCartActions()
  const { account, refresh } = useAccount()
  const [phase, setPhase] = useState<ReturnPhase>('checking')
  const [placed, setPlaced] = useState<PlacedOrder | null>(null)
  /** Ophogen = opnieuw controleren (knop, of na "al betaald" bij opnieuw betalen). */
  const [round, setRound] = useState(0)
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  /** Het nette bestelnummer volgens de server; `null` tot er betaald is. */
  const [orderNumber, setOrderNumber] = useState<string | null>(null)

  // De nieuwste functies, zonder dat het controleren opnieuw start als het account binnenkomt.
  const latest = useRef({ clearCart, refresh, signedIn: Boolean(account) })
  latest.current = { clearCart, refresh, signedIn: Boolean(account) }

  useEffect(() => {
    const controller = new AbortController()
    let timer: number | undefined
    const started = Date.now()
    setPhase('checking')

    const confirmPaid = (s: OrderStatusResponse) => {
      const pending = takePending(orderId)
      const stored = readPlaced()
      const draft = pending ?? (stored?.orderId === orderId ? stored : null)
      if (draft) {
        // Bedragen van de server winnen van wat de site vóór het betalen dacht.
        const confirmed: PlacedOrder = {
          ...draft,
          total: fromCents(s.totalCents),
          gift: s.gift,
          orderNumber: s.orderNumber,
          askSource: s.askSource,
          accountOrder: { ...draft.accountOrder, shipping: fromCents(s.shippingCents) },
        }
        savePlaced(confirmed)
        setPlaced(confirmed)
      }
      // Alleen de eerste keer, en alleen als de mand nog precies deze bestelling is: wie na het
      // betalen (of tijdens, in een ander tabblad) iets nieuws in de mand deed, houdt die mand.
      if (pending) {
        clearAttempt()
        if (cartBelongsTo(pending, storedCartLines())) latest.current.clearCart()
        if (latest.current.signedIn) void latest.current.refresh()
      }
      setPhase('paid')
    }

    const check = async () => {
      try {
        const s = await fetchOrderStatus(orderId, token, { signal: controller.signal })
        setOrderNumber(s.orderNumber)
        if (s.status === 'betaald') return confirmPaid(s)
        if (s.status === 'mislukt') return setPhase('failed')
        if (s.status === 'verlopen') return setPhase('expired')
        if (Date.now() - started >= POLL_LIMIT_MS) return setPhase('waiting')
        timer = window.setTimeout(() => void check(), POLL_INTERVAL_MS)
      } catch (err) {
        if (isAbortError(err)) return
        setPhase(err instanceof ApiError && err.status === 404 ? 'notFound' : 'error')
      }
    }

    void check()
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [orderId, token, round])

  const recheck = () => setRound((r) => r + 1)

  const retry = async () => {
    if (retrying) return
    setRetrying(true)
    setRetryError(null)
    try {
      const { paymentUrl } = await retryPayment(orderId, token)
      // Blijft 'retrying': de pagina gaat weg naar Mollie.
      window.location.assign(paymentUrl)
    } catch (err) {
      setRetrying(false)
      // Intussen toch betaald (bijv. een trage webhook): opnieuw kijken in plaats van een fout tonen.
      if (err instanceof ApiError && err.code === 'already_paid') return recheck()
      setRetryError(errorMessage(err) ?? 'Opnieuw betalen lukte niet. Probeer het zo nog eens.')
    }
  }

  return { phase, placed, orderNumber, signedIn: Boolean(account), recheck, retry, retrying, retryError }
}
